import { CinemaDto } from '@cineamo/api/models/cinema/CinemaDto.types';
import { ContentDto } from '@cineamo/api/models/content/ContentDto.types';
import { ShowingDto } from '@cineamo/api/models/showing/ShowingDto.types';

import { ActiveFilters } from '@/types/filter/ActiveFilters.types';

import { ShowingApiGroup } from '@cineamo/api/resources/showing/showing-api.group';

import { CachedContentData, getCachedContentData } from '@/hooks/useCachedContentData';
import { applyClientFilters, getFutureShowingsFiltered } from '@/hooks/useFutureShowings';

import { formatDate_yyyyMMddTHHmmssZ } from '@/utils/date-helper';

import {
    programDayAtHour,
    programDayEnd,
    programDayKeyFromToday,
    programDayKeys,
    programDayStart,
    todayProgramDayKey
} from './program-day';

type GetShowingsOptions = {
    cinemaId: number;
    startDate: Date | string;
    /** The cinema's zone — every day boundary below is read on its clock, never the viewer's. */
    timeZone: string;
    filter?: ActiveFilters;
};

export const getStartDatetime = (options: GetShowingsOptions) => {
    const startDate =
        options.startDate === 'all'
            ? todayProgramDayKey(options.timeZone)
            : options.startDate === 'soon'
              ? programDayKeyFromToday(14, options.timeZone)
              : options.startDate;
    const startDatetime =
        'startTime' in (options.filter ?? {})
            ? programDayAtHour(startDate, Number(options.filter?.startTime), options.timeZone)
            : programDayStart(startDate, options.timeZone);
    return formatDate_yyyyMMddTHHmmssZ(startDatetime);
};

export const getEndDatetime = (options: GetShowingsOptions) => {
    if (['all', 'soon'].includes(options.startDate.toString())) {
        return undefined;
    }
    const endDatetime =
        'endTime' in (options.filter ?? {})
            ? programDayAtHour(options.startDate, Number(options.filter?.endTime), options.timeZone)
            : programDayEnd(options.startDate, options.timeZone);

    return formatDate_yyyyMMddTHHmmssZ(endDatetime);
};

export async function getContentsWithShowings(
    options: GetShowingsOptions,
    cinema: CinemaDto,
    locale: string
): Promise<CachedContentData[]> {
    const startDatetime = getStartDatetime(options);
    const endDatetime = getEndDatetime(options);
    const showings = await getFutureShowingsFiltered(
        options.cinemaId,
        {
            startDatetime,
            endDatetime,
            group: ShowingApiGroup.Content,
            ...options.filter
        },
        null,
        { locale }
    );

    return await Promise.all(
        showings
            .map((showing) => showing._embedded?.content)
            .filter((content): content is ContentDto => !!content)
            .map((content) =>
                getCachedContentData({
                    content,
                    cinema: cinema,
                    locale
                })
            )
    );
}

const filterShowingsForDay = (showings: ShowingDto[], dayKey: string, timeZone: string): ShowingDto[] => {
    const dayStart = programDayStart(dayKey, timeZone);
    const dayEnd = programDayEnd(dayKey, timeZone);

    return showings.filter((showing) => {
        const showingDate = new Date(showing.startDatetime);
        return showingDate >= dayStart && showingDate <= dayEnd;
    });
};

const filterShowingsAfterDate = (showings: ShowingDto[], dayKey: string, timeZone: string): ShowingDto[] => {
    const startDate = programDayStart(dayKey, timeZone);

    return showings.filter((showing) => {
        const showingDate = new Date(showing.startDatetime);
        return showingDate >= startDate;
    });
};

export async function getShowingCountsForDays(options: {
    contentId?: number;
    cinemaId: number;
    daysCount: number;
    timeZone: string;
    filter?: ActiveFilters;
    /**
     * Raw, unfiltered `showings-future` response of the cinema; when provided, skips the HTTP
     * fetch. Not compatible with tag filters (`showingTagIds`/`hasShowingTags`), which are only
     * applied server-side.
     */
    showings?: ShowingDto[];
}): Promise<number[]> {
    const showingsQuery = {
        contentId: options.contentId,
        ...options.filter
    };
    const allShowings = options.showings
        ? await applyClientFilters(options.showings, showingsQuery)
        : await getFutureShowingsFiltered(options.cinemaId, showingsQuery);

    // Group showings by day for the specified period
    const dayKeys = programDayKeys(options.timeZone, options.daysCount + 1);
    const showingsByDate = dayKeys
        .slice(0, options.daysCount)
        .map((dayKey) => filterShowingsForDay(allShowings, dayKey, options.timeZone));

    // Add "soon" showings as the final element
    const soonShowings = filterShowingsAfterDate(allShowings, dayKeys[options.daysCount], options.timeZone);
    showingsByDate.push(soonShowings);

    return showingsByDate.map((showings) => showings.length);
}
